import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { HeartHandshake, Code2, Users, Facebook, Link2, Mail, Check, Share2, Download, ClipboardCopy } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from './ui/dialog';
import { toast } from 'sonner';
import { api } from '../lib/api';

const DEFAULTS = {
  share_cta_label: 'Share PITA',
  support_cta_label: 'Support PITA',
  embed_cta_label: 'Request PITA Embed',
  supporters_cta_label: 'View Supporters by Ward',
  share_title: 'Support the Public Issue Tracker App (PITA)',
  share_message: 'Support the Public Issue Tracker App (PITA) and help make City Hall accountable to the people. PITA helps residents report local municipal issues, map concerns, track follow-up, and make visible patterns that should not be ignored.',
  share_tagline: 'Report it. Map it. Track it. Get answers.',
  share_image_url: '/api/uploads/pita-logo.png',
  share_destination: '/support-pita',
  disclaimer: 'This is not an official City of Kawartha Lakes service portal.',
  logo_url: '/api/uploads/pita-logo.png',
  logo_alt: 'Public Issue Tracker App (PITA) logo',
  logo_placements: { home_hero: true, pita_page: true, share_dialog: true, network: true, support: true, print_assets: true, share_cards: true },
};

export const usePitaSettings = () => {
  const [s, setS] = useState(DEFAULTS);
  useEffect(() => {
    api.get('/pita/settings').then((r) => setS({ ...DEFAULTS, ...r.data })).catch(() => {});
  }, []);
  return s;
};

// Returns { url, alt } for the PITA logo at a given placement, or null when hidden there.
export const usePitaLogo = (placement) => {
  const s = usePitaSettings();
  if (!s.logo_url) return null;
  if (placement && s.logo_placements && s.logo_placements[placement] === false) return null;
  const url = s.logo_url.startsWith('http') ? s.logo_url : `${backendBase}${s.logo_url}`;
  return { url, alt: s.logo_alt || 'Public Issue Tracker App (PITA) logo' };
};

const backendBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
export const resolveShareImage = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${backendBase}${url}`;
};

// Supporter Share Card info (approved wording + generated image URLs). Download only; nothing auto-posts.
export const usePitaShareCard = () => {
  const [card, setCard] = useState(null);
  useEffect(() => {
    api.get('/pita/share-card').then((r) => setCard(r.data)).catch(() => {});
  }, []);
  return card;
};

// Clipboard helper with a fallback for restricted contexts.
export const copyTextRobust = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
};

export const copyShareCardText = async (card) => {
  const ok = await copyTextRobust(card.share_text || '');
  if (ok) toast.success('Share text copied. Paste it anywhere you like.');
  else toast.error('Could not copy. Please copy the text manually.');
};

// Compact share card block for Support PITA / PITA Supporters pages.
// Renders nothing until a card image has been generated (no empty placeholders).
export const PitaShareCardBlock = () => {
  const card = usePitaShareCard();
  const imageUrl = card?.images?.square || card?.images?.landscape || '';
  if (!card || !imageUrl) return null;
  return (
    <div className="rounded-2xl border border-[#C9A84C]/40 bg-[#1a2744] p-5 sm:p-6" data-testid="pita-share-card-block">
      <div className="flex flex-col sm:flex-row gap-5">
        <img
          src={resolveShareImage(imageUrl)}
          alt="PITA supporter share card preview"
          className="rounded-xl w-full sm:w-44 h-auto sm:h-44 object-cover border border-white/10 shrink-0"
          data-testid="pita-share-card-preview"
        />
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-bold uppercase tracking-widest text-[#C9A84C]">Supporter Share Card</p>
          <h3 className="font-serif font-black text-white text-lg sm:text-xl mt-1">Post PITA to your own page</h3>
          <p className="text-slate-300 text-sm mt-2 leading-relaxed">
            Download the ready-made share card and post it to Facebook or send it by email, along with the approved
            share text. Nothing is posted automatically.
          </p>
          <div className="flex flex-wrap gap-2.5 mt-4">
            <Button asChild className="h-10 bg-[#C9A84C] hover:bg-[#b8973f] text-[#1a2744] font-bold btn-press" data-testid="pita-share-card-download">
              <a href={resolveShareImage(imageUrl)} download="pita-share-card.png">
                <Download className="h-4 w-4 mr-1.5" /> Download Share Card
              </a>
            </Button>
            <Button variant="outline" onClick={() => copyShareCardText(card)} className="h-10 border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white font-bold btn-press" data-testid="pita-share-card-copy">
              <ClipboardCopy className="h-4 w-4 mr-1.5" /> Copy Share Text
            </Button>
            <PitaShareDialog
              trigger={
                <Button variant="outline" className="h-10 border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white font-bold btn-press" data-testid="pita-share-card-share">
                  <Share2 className="h-4 w-4 mr-1.5" /> Share PITA
                </Button>
              }
            />
          </div>
          <p className="text-[11px] text-slate-400 mt-3">{card.disclaimer}</p>
        </div>
      </div>
    </div>
  );
};

export const PitaShareDialog = ({ trigger, settings }) => {
  const fallback = usePitaSettings();
  const s = settings || fallback;
  const card = usePitaShareCard();
  const cardImage = card?.images?.square || card?.images?.landscape || '';
  const [copied, setCopied] = useState(false);
  const [fbNote, setFbNote] = useState(false);

  const destination = s.share_destination || '/support-pita';
  const shareUrl = `${window.location.origin}${destination.startsWith('/') ? destination : `/${destination}`}`;
  const fullText = `${s.share_message}\n\n${s.share_tagline}\n\n${shareUrl}\n\n${s.disclaimer}`;

  const copyText = async (text) => copyTextRobust(text);

  const onFacebook = async () => {
    const ok = await copyText(fullText);
    setFbNote(true);
    if (ok) toast.success('Message copied. Paste it into Facebook before posting.');
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank', 'noopener,noreferrer,width=600,height=500');
  };

  const onCopyLink = async () => {
    const ok = await copyText(fullText);
    if (ok) {
      setCopied(true);
      toast.success('Share message and link copied');
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error('Could not copy. Please copy the text manually.');
    }
  };

  const onEmail = () => {
    const subject = encodeURIComponent(s.share_title);
    const body = encodeURIComponent(fullText);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  return (
    <Dialog>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-md" data-testid="pita-share-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif font-black text-[#1a2744]">{s.share_title}</DialogTitle>
          <DialogDescription className="sr-only">Share the Public Issue Tracker App (PITA)</DialogDescription>
        </DialogHeader>
        <div className="flex justify-center">
          {(!s.logo_placements || s.logo_placements.share_dialog !== false) && (
            <img
              src={resolveShareImage(s.logo_url || s.share_image_url)}
              alt={s.logo_alt || 'Public Issue Tracker App (PITA) logo'}
              className="h-32 w-32 rounded-2xl object-contain"
              data-testid="pita-share-image"
            />
          )}
        </div>
        <p className="text-sm text-slate-600 leading-relaxed" data-testid="pita-share-message">{s.share_message}</p>
        <p className="text-sm font-bold text-[#1a2744]" data-testid="pita-share-tagline">{s.share_tagline}</p>
        <div className="grid grid-cols-1 gap-2">
          <Button onClick={onFacebook} className="h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press justify-start" data-testid="pita-share-facebook">
            <Facebook className="h-4 w-4 mr-2" /> Share on Facebook
          </Button>
          {fbNote && (
            <p className="text-xs text-slate-500 leading-snug px-1" data-testid="pita-share-fb-note">
              Your message has been copied. Paste it into Facebook before posting.
            </p>
          )}
          <Button variant="outline" onClick={onCopyLink} className="h-11 border-[#1a2744]/30 text-[#1a2744] font-bold btn-press justify-start" data-testid="pita-share-copy">
            {copied ? <Check className="h-4 w-4 mr-2" /> : <Link2 className="h-4 w-4 mr-2" />} Copy Message + Link
          </Button>
          <Button variant="outline" onClick={onEmail} className="h-11 border-[#1a2744]/30 text-[#1a2744] font-bold btn-press justify-start" data-testid="pita-share-email">
            <Mail className="h-4 w-4 mr-2" /> Share by Email
          </Button>
          {cardImage && (
            <Button asChild variant="outline" className="h-11 border-[#1a2744]/30 text-[#1a2744] font-bold btn-press justify-start" data-testid="pita-share-download-card">
              <a href={resolveShareImage(cardImage)} download="pita-share-card.png">
                <Download className="h-4 w-4 mr-2" /> Download Share Card
              </a>
            </Button>
          )}
        </div>
        <p className="text-[11px] text-slate-400" data-testid="pita-share-disclaimer">{s.disclaimer}</p>
      </DialogContent>
    </Dialog>
  );
};

export const PitaCTA = ({ compact = false }) => {
  const s = usePitaSettings();

  return (
    <div className="rounded-2xl border border-[#C9A84C]/40 bg-[#1a2744] p-5 sm:p-7" data-testid="pita-cta-block">
      <div className="flex items-start gap-4">
        {(!s.logo_placements || s.logo_placements.network !== false) && (
          <div className="bg-white rounded-xl p-2 shrink-0 h-16 w-16 sm:h-20 sm:w-20 flex items-center justify-center">
            <img
              src={resolveShareImage(s.logo_url || s.share_image_url)}
              alt={s.logo_alt || 'Public Issue Tracker App (PITA) logo'}
              className="max-h-full max-w-full object-contain"
              data-testid="pita-cta-logo"
            />
          </div>
        )}
        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-widest text-[#C9A84C]">Public Issue Tracker App (PITA)</p>
          <h2 className="font-serif font-black text-white text-xl sm:text-2xl mt-1.5">Help PITA grow across Kawartha Lakes</h2>
        </div>
      </div>
      {!compact && (
        <p className="text-slate-300 text-sm mt-3 leading-relaxed">
          Residents, community groups, Councillors, and candidates can share PITA, support it publicly, or request an
          embed for their own website. Everything is approval-based and privacy-safe.
        </p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mt-4">
        <PitaShareDialog
          settings={s}
          trigger={
            <Button className="h-11 bg-[#C9A84C] hover:bg-[#b8973f] text-[#1a2744] font-bold btn-press justify-start" data-testid="pita-cta-share">
              <Share2 className="h-4 w-4 mr-2" /> {s.share_cta_label}
            </Button>
          }
        />
        <Button asChild variant="outline" className="h-11 border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white font-bold btn-press justify-start" data-testid="pita-cta-embed">
          <Link to="/pita/request-access"><Code2 className="h-4 w-4 mr-2" /> {s.embed_cta_label}</Link>
        </Button>
        <Button asChild variant="outline" className="h-11 border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white font-bold btn-press justify-start" data-testid="pita-cta-support">
          <Link to="/support-pita"><HeartHandshake className="h-4 w-4 mr-2" /> {s.support_cta_label}</Link>
        </Button>
        <Button asChild variant="outline" className="h-11 border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white font-bold btn-press justify-start" data-testid="pita-cta-supporters">
          <Link to="/pita-supporters"><Users className="h-4 w-4 mr-2" /> {s.supporters_cta_label}</Link>
        </Button>
      </div>
      <p className="text-[11px] text-slate-400 mt-4" data-testid="pita-cta-disclaimer">This is not an official City of Kawartha Lakes service portal.</p>
    </div>
  );
};
